/**
 * Phase 8: Evidence Builder Panel
 *
 * Inline tab component (within DocumentPanel) that helps users:
 *  - View USCIS evidence criteria for EB-2 NIW (Dhanasar framework) and EB-1A (10 criteria)
 *  - Upload supporting documents per criterion
 *  - Download or delete uploaded evidence files
 *  - Generate an overall strategy assessment with per-criterion strength ratings
 *  - View prioritized recommendations for strengthening their petition
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE as API } from '../lib/apiBase';

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok)
    throw Object.assign(
      new Error(data.detail?.message || data.detail || 'Request failed'),
      { data, status: res.status }
    );
  return data;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PATHWAY_NAMES = {
  'eb2-niw': 'EB-2 National Interest Waiver',
  'eb1a': 'EB-1A Extraordinary Ability',
  'o1-nonimmigrant': 'O-1 Extraordinary Ability / Achievement',
  'asylum': 'Asylum & Humanitarian',
  'perm-employer': 'PERM Labor Certification',
};

/** Pathways that use document checklist mode (required/optional tracking, lighter strategy UI) */
const CHECKLIST_PATHWAYS = new Set(['asylum', 'perm-employer']);

const STRENGTH_CONFIG = {
  strong: { label: 'Strong', color: '#166534', bg: '#F0FDF4', border: '#22C55E', icon: '💪' },
  moderate: { label: 'Moderate', color: '#854D0E', bg: '#FEFCE8', border: '#EAB308', icon: '📊' },
  needs_attention: { label: 'Needs Attention', color: '#991B1B', bg: '#FEF2F2', border: '#EF4444', icon: '⚠️' },
};

const CRITERION_STATUS_CONFIG = {
  strong: { label: 'Strong', color: '#166534', bg: '#DCFCE7', icon: '✅' },
  needs_work: { label: 'Needs Work', color: '#854D0E', bg: '#FEF9C3', icon: '⚡' },
  insufficient_evidence: { label: 'No Evidence', color: '#6B7280', bg: '#F3F4F6', icon: '○' },
};

const EVAL_STATUS_CONFIG = {
  pending: { label: 'Pending Review', color: '#6B7280', bg: '#F3F4F6' },
  meets: { label: '✓ Meets', color: '#166534', bg: '#DCFCE7' },
  partially_meets: { label: '~ Partially Meets', color: '#854D0E', bg: '#FEF9C3' },
  does_not_meet: { label: '✗ Does Not Meet', color: '#991B1B', bg: '#FEE2E2' },
};

const ALLOWED_EXTENSIONS = '.pdf,.docx,.doc,.jpg,.jpeg,.png,.tiff,.txt';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const StatusBadge = ({ status, config }) => {
  const cfg = config[status] || config[Object.keys(config)[0]];
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ color: cfg.color, backgroundColor: cfg.bg }}
    >
      {cfg.icon && <span>{cfg.icon}</span>}
      {cfg.label}
    </span>
  );
};

const EvalBadge = ({ status }) => {
  const cfg = EVAL_STATUS_CONFIG[status] || EVAL_STATUS_CONFIG.pending;
  return (
    <span
      className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ color: cfg.color, backgroundColor: cfg.bg }}
    >
      {cfg.label}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function EvidenceBuilderPanel({ pathwayId, workflowStatus }) {
  const [criteria, setCriteria] = useState([]);
  const [evidenceMap, setEvidenceMap] = useState({}); // criterion_id -> [items]
  const [strategy, setStrategy] = useState(null);
  const [expandedCriteria, setExpandedCriteria] = useState(new Set());
  const [expandedExamples, setExpandedExamples] = useState(new Set());
  const [uploading, setUploading] = useState(new Set()); // criterion_ids uploading
  const [generatingStrategy, setGeneratingStrategy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadErrors, setUploadErrors] = useState({}); // criterion_id -> error
  const [reusableItems, setReusableItems] = useState([]);
  const [reusingId, setReusingId] = useState(null);
  const [reuseCriterionId, setReuseCriterionId] = useState('');
  const fileInputRefs = useRef({});

  const isSupportedPathway = [
    'eb2-niw',
    'eb1a',
    'o1-nonimmigrant',
    'asylum',
    'perm-employer',
  ].includes(pathwayId);
  const isChecklistMode = CHECKLIST_PATHWAYS.has(pathwayId);

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const fetchCriteria = useCallback(async () => {
    try {
      const data = await apiFetch(`/api/evidence/${pathwayId}/criteria`);
      setCriteria(data.criteria || []);
    } catch (err) {
      if (err.status !== 404) {
        setError(err.message || 'Failed to load criteria.');
      }
    }
  }, [pathwayId]);

  const fetchEvidence = useCallback(async () => {
    try {
      const data = await apiFetch(`/api/evidence/${pathwayId}`);
      setEvidenceMap(data.by_criterion || {});
    } catch (err) {
      if (err.status !== 404) {
        setError(err.message || 'Failed to load evidence.');
      }
    }
  }, [pathwayId]);

  const fetchStrategy = useCallback(async () => {
    if (CHECKLIST_PATHWAYS.has(pathwayId)) {
      setStrategy(null);
      return;
    }
    try {
      const data = await apiFetch(`/api/evidence/${pathwayId}/strategy`);
      setStrategy(data);
    } catch (err) {
      // 404 is expected if no strategy generated yet
      if (err.status !== 404) {
        console.warn('Strategy fetch error:', err.message);
      }
    }
  }, [pathwayId]);

  const fetchReusable = useCallback(async () => {
    try {
      const data = await apiFetch(
        `/api/evidence/reusable?exclude_pathway_id=${encodeURIComponent(pathwayId)}`
      );
      setReusableItems(data.items || []);
    } catch (err) {
      console.warn('Reusable evidence fetch error:', err.message);
    }
  }, [pathwayId]);

  useEffect(() => {
    if (!isSupportedPathway) {
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError('');
      await Promise.all([
        fetchCriteria(),
        fetchEvidence(),
        fetchStrategy(),
        fetchReusable(),
      ]);
      setLoading(false);
    };
    load();
  }, [
    pathwayId,
    isSupportedPathway,
    fetchCriteria,
    fetchEvidence,
    fetchStrategy,
    fetchReusable,
  ]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const toggleCriterion = (criterionId) => {
    setExpandedCriteria((prev) => {
      const next = new Set(prev);
      next.has(criterionId) ? next.delete(criterionId) : next.add(criterionId);
      return next;
    });
  };

  const toggleExamples = (criterionId) => {
    setExpandedExamples((prev) => {
      const next = new Set(prev);
      next.has(criterionId) ? next.delete(criterionId) : next.add(criterionId);
      return next;
    });
  };

  const handleFileSelect = async (criterionId, file, contextDescription) => {
    if (!file) return;

    // Client-side size check (25MB)
    if (file.size > 25 * 1024 * 1024) {
      setUploadErrors((prev) => ({
        ...prev,
        [criterionId]: `File too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum is 25 MB.`,
      }));
      return;
    }

    setUploadErrors((prev) => ({ ...prev, [criterionId]: '' }));
    setUploading((prev) => new Set(prev).add(criterionId));

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (contextDescription) {
        formData.append('context_description', contextDescription);
      }

      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API}/api/evidence/${pathwayId}/${criterionId}/upload`,
        {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail?.message || data.detail || 'Upload failed');
      }

      // Refresh evidence list
      await fetchEvidence();
    } catch (err) {
      setUploadErrors((prev) => ({
        ...prev,
        [criterionId]: err.message || 'Upload failed.',
      }));
    } finally {
      setUploading((prev) => {
        const next = new Set(prev);
        next.delete(criterionId);
        return next;
      });
      // Reset file input
      if (fileInputRefs.current[criterionId]) {
        fileInputRefs.current[criterionId].value = '';
      }
    }
  };

  const handleDelete = async (criterionId, evidenceId, fileName) => {
    if (!window.confirm(`Delete "${fileName}"? This cannot be undone.`)) return;
    try {
      await apiFetch(`/api/evidence/${pathwayId}/${criterionId}/${evidenceId}`, {
        method: 'DELETE',
      });
      await fetchEvidence();
    } catch (err) {
      setError(err.message || 'Failed to delete evidence item.');
    }
  };

  const handleDownload = (evidenceId, fileName) => {
    const token = localStorage.getItem('token');
    // Build download URL and trigger via anchor
    const url = `${API}/api/evidence/${evidenceId}/download`;
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    // Can't set auth headers via anchor; use fetch instead
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((res) => res.blob())
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        link.href = objectUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      })
      .catch(() => setError('Download failed.'));
  };

  const handleGenerateStrategy = async () => {
    setGeneratingStrategy(true);
    setError('');
    try {
      const data = await apiFetch(`/api/evidence/${pathwayId}/strategy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      setStrategy(data.strategy);
    } catch (err) {
      setError(err.message || 'Failed to generate strategy summary.');
    } finally {
      setGeneratingStrategy(false);
    }
  };

  const handleReuseEvidence = async (sourceEvidenceId) => {
    if (!reuseCriterionId) {
      setError('Select a criterion before reusing evidence.');
      return;
    }
    setReusingId(sourceEvidenceId);
    setError('');
    try {
      await apiFetch('/api/evidence/reuse', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source_evidence_id: sourceEvidenceId,
          pathway_id: pathwayId,
          criterion_id: reuseCriterionId,
        }),
      });
      await fetchEvidence();
      await fetchReusable();
    } catch (err) {
      setError(err.message || 'Failed to reuse evidence.');
    } finally {
      setReusingId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Unsupported pathway guard
  // ---------------------------------------------------------------------------

  if (!isSupportedPathway) {
    return (
      <div className="text-center py-10 text-gray-500">
        <i className="fas fa-folder-open text-4xl mb-4 block text-gray-300"></i>
        <p className="font-medium text-gray-700">Evidence Builder not available</p>
        <p className="text-sm mt-2">
          The Evidence Builder is available for EB-2 NIW, EB-1A, O-1, Asylum, and PERM pathways.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-gray-400">
        <i className="fas fa-spinner fa-spin mr-2"></i> Loading evidence builder...
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const pathwayName = PATHWAY_NAMES[pathwayId] || pathwayId;
  const isEB1A = pathwayId === 'eb1a';
  const isO1 = pathwayId === 'o1-nonimmigrant';

  const headerSubtitle = isChecklistMode
    ? 'Upload required and optional documents for each checklist item.'
    : isEB1A
      ? 'Upload evidence for each criterion. You need to meet at least 3 of 10.'
      : isO1
        ? 'Upload evidence for O-1A (8 criteria) and/or O-1B Arts (6 criteria). Meet at least 3.'
        : 'Upload supporting documents for each Dhanasar evidence category.';

  return (
    <div className="space-y-5">
      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg flex items-center gap-2">
          <i className="fas fa-exclamation-circle"></i>
          {error}
          <button className="ml-auto text-red-400 hover:text-red-600" onClick={() => setError('')}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-bold text-gray-900">
            <i className="fas fa-folder-open mr-2 text-yellow-500"></i>
            {isChecklistMode ? 'Document Checklist' : 'Evidence Builder'} — {pathwayName}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {headerSubtitle}
          </p>
        </div>
        <span className="text-xs bg-amber-100 text-amber-700 border border-amber-300 px-2 py-1 rounded-full font-medium whitespace-nowrap">
          <i className="fas fa-balance-scale mr-1"></i>Not legal advice
        </span>
      </div>

      {/* Cross-pathway reuse */}
      {reusableItems.length > 0 && (
        <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div>
              <p className="text-sm font-semibold text-gray-800">Reuse evidence from other pathways</p>
              <p className="text-xs text-gray-500">Attach a file you already uploaded without re-uploading.</p>
            </div>
            <select
              value={reuseCriterionId}
              onChange={(e) => setReuseCriterionId(e.target.value)}
              className="border border-gray-300 rounded-lg text-sm px-3 py-2 bg-white"
            >
              <option value="">Select criterion…</option>
              {criteria.map((c) => (
                <option key={c.criterion_id} value={c.criterion_id}>
                  {c.criterion_name}
                </option>
              ))}
            </select>
          </div>
          <ul className="space-y-2 max-h-40 overflow-auto">
            {reusableItems.slice(0, 8).map((item) => (
              <li
                key={item.id}
                className="flex items-center justify-between gap-3 text-sm bg-white border border-gray-200 rounded-lg px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="font-medium text-gray-800 truncate">
                    {item.filename || item.file_name || 'Evidence file'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    From {item.source_pathway_name || item.source_pathway_id}
                    {item.criterion_id ? ` · ${item.criterion_id}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={!!reusingId || !reuseCriterionId}
                  onClick={() => handleReuseEvidence(item.id)}
                  className="flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg bg-yellow-500 text-black hover:bg-yellow-400 disabled:opacity-50"
                >
                  {reusingId === item.id ? 'Reusing…' : 'Reuse'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Strategy Assessment Card — full mode only (EB-2 / EB-1A / O-1) */}
      {!isChecklistMode && (strategy ? (
        <div
          className="rounded-xl border p-4"
          style={{
            borderColor: STRENGTH_CONFIG[strategy.overall_strength]?.border || '#E5E7EB',
            backgroundColor: STRENGTH_CONFIG[strategy.overall_strength]?.bg || '#F9FAFB',
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-xl">{STRENGTH_CONFIG[strategy.overall_strength]?.icon}</span>
              <div>
                <p className="text-sm font-bold text-gray-800">Strategy Assessment</p>
                <p className="text-xs text-gray-500">
                  {isEB1A
                    ? `${strategy.criteria_met_count} of 10 criteria met strongly (need ${strategy.criteria_needed || 3})`
                    : `${strategy.criteria_met_count} of ${Object.keys(strategy.criterion_evaluations || {}).length} categories strong`}
                </p>
              </div>
            </div>
            <StatusBadge
              status={strategy.overall_strength}
              config={STRENGTH_CONFIG}
            />
          </div>

          <p className="text-sm text-gray-700 mb-3">{strategy.overall_assessment}</p>

          {/* Per-criterion chips */}
          {strategy.criterion_evaluations && Object.keys(strategy.criterion_evaluations).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {criteria.map((c) => {
                const cStatus = strategy.criterion_evaluations[c.criterion_id];
                if (!cStatus) return null;
                const cfg = CRITERION_STATUS_CONFIG[cStatus] || CRITERION_STATUS_CONFIG.insufficient_evidence;
                const shortName = c.criterion_name.replace(/^Criterion \d+:\s*/, '').split(/[—:]/)[0].trim();
                return (
                  <span
                    key={c.criterion_id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{ color: cfg.color, backgroundColor: cfg.bg }}
                    title={c.criterion_name}
                  >
                    <span>{cfg.icon}</span>
                    {shortName.length > 20 ? shortName.slice(0, 20) + '…' : shortName}
                  </span>
                );
              })}
            </div>
          )}

          {/* Recommendations */}
          {strategy.prioritized_recommendations?.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-semibold text-gray-600 mb-1.5">Recommendations:</p>
              <ul className="space-y-1">
                {strategy.prioritized_recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-gray-700">
                    <span className="text-yellow-500 mt-0.5">→</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-3 pt-2 border-t border-gray-200 flex items-center justify-between">
            <span className="text-xs text-gray-400">
              Generated {strategy.created_at ? new Date(strategy.created_at).toLocaleDateString() : '—'}
            </span>
            <button
              onClick={handleGenerateStrategy}
              disabled={generatingStrategy}
              className="text-xs px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {generatingStrategy ? (
                <><i className="fas fa-spinner fa-spin mr-1"></i>Regenerating...</>
              ) : (
                <><i className="fas fa-sync-alt mr-1"></i>Regenerate</>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
          <i className="fas fa-chart-bar text-gray-300 text-3xl mb-2 block"></i>
          <p className="text-sm text-gray-600 mb-3">
            Upload evidence for your criteria, then generate an assessment.
          </p>
          <button
            onClick={handleGenerateStrategy}
            disabled={generatingStrategy}
            className="px-4 py-2 bg-yellow-500 text-white text-sm font-semibold rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50"
          >
            {generatingStrategy ? (
              <><i className="fas fa-spinner fa-spin mr-2"></i>Generating...</>
            ) : (
              <><i className="fas fa-chart-bar mr-2"></i>Generate Strategy Assessment</>
            )}
          </button>
        </div>
      ))}

      {/* Checklist progress — asylum / PERM */}
      {isChecklistMode && (() => {
        const required = criteria.filter((c) => c.required);
        const requiredDone = required.filter((c) => (evidenceMap[c.criterion_id] || []).length > 0).length;
        const optionalDone = criteria
          .filter((c) => !c.required)
          .filter((c) => (evidenceMap[c.criterion_id] || []).length > 0).length;
        return (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-gray-800 mb-1">Checklist progress</p>
            <p className="text-xs text-gray-600">
              Required documents complete: {requiredDone} of {required.length}
              {optionalDone > 0 ? ` · ${optionalDone} optional uploaded` : ''}
            </p>
          </div>
        );
      })()}

      {/* Criteria Accordion */}
      <div className="space-y-2">
        {criteria.map((criterion) => {
          const cid = criterion.criterion_id;
          const isExpanded = expandedCriteria.has(cid);
          const showExamples = expandedExamples.has(cid);
          const items = evidenceMap[cid] || [];
          const isUploading = uploading.has(cid);
          const uploadError = uploadErrors[cid];
          const cStatus = strategy?.criterion_evaluations?.[cid];
          const statusCfg = cStatus ? CRITERION_STATUS_CONFIG[cStatus] : null;

          // Short display name
          const displayName = criterion.criterion_name
            .replace(/^Criterion \d+:\s*/, '')
            .split(/[—]/)[0]
            .trim();

          return (
            <div
              key={cid}
              className="border border-gray-200 rounded-xl overflow-hidden"
            >
              {/* Criterion header (clickable to expand) */}
              <button
                onClick={() => toggleCriterion(cid)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-gray-400 flex-shrink-0">
                    <i className={`fas ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'} text-xs`}></i>
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{displayName}</p>
                    {criterion.meeting_threshold && (
                      <p className="text-xs text-gray-400">{criterion.meeting_threshold}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                  {items.length > 0 && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                      {items.length} file{items.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {statusCfg && (
                    <span
                      className="text-xs px-2 py-0.5 rounded-full font-semibold"
                      style={{ color: statusCfg.color, backgroundColor: statusCfg.bg }}
                    >
                      {statusCfg.icon} {statusCfg.label}
                    </span>
                  )}
                </div>
              </button>

              {/* Expanded content */}
              {isExpanded && (
                <div className="border-t border-gray-100 px-4 pb-4 pt-3 space-y-4">
                  {/* USCIS Standard */}
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                    <p className="text-xs font-semibold text-blue-700 mb-1">
                      <i className="fas fa-balance-scale mr-1"></i>USCIS Standard
                    </p>
                    <p className="text-xs text-blue-900 leading-relaxed">{criterion.uscis_standard}</p>
                  </div>

                  {/* Qualifying Examples (collapsible) */}
                  {criterion.qualifying_examples?.length > 0 && (
                    <div>
                      <button
                        onClick={() => toggleExamples(cid)}
                        className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        <i className={`fas ${showExamples ? 'fa-chevron-down' : 'fa-chevron-right'} text-xs`}></i>
                        Qualifying Examples ({criterion.qualifying_examples.length})
                      </button>
                      {showExamples && (
                        <ul className="mt-2 space-y-1 pl-4">
                          {criterion.qualifying_examples.map((ex, i) => (
                            <li key={i} className="flex items-start gap-1.5 text-xs text-gray-600">
                              <span className="text-yellow-500 mt-0.5 flex-shrink-0">•</span>
                              {ex}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* Uploaded Files */}
                  {items.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-600">
                        Uploaded Evidence ({items.length})
                      </p>
                      {items.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-gray-800 truncate">{item.file_name}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-gray-400">{item.file_size_kb} KB</span>
                              <EvalBadge status={item.evaluation_status} />
                              {item.context_description && (
                                <span className="text-xs text-gray-400 italic truncate max-w-[120px]">
                                  {item.context_description}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                            <button
                              onClick={() => handleDownload(item.id, item.file_name)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Download"
                            >
                              <i className="fas fa-download text-xs"></i>
                            </button>
                            <button
                              onClick={() => handleDelete(cid, item.id, item.file_name)}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                              title="Delete"
                            >
                              <i className="fas fa-trash text-xs"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Upload error */}
                  {uploadError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded-lg">
                      <i className="fas fa-exclamation-circle mr-1"></i>{uploadError}
                    </div>
                  )}

                  {/* Upload area */}
                  <UploadArea
                    criterionId={cid}
                    isUploading={isUploading}
                    fileInputRef={(el) => { fileInputRefs.current[cid] = el; }}
                    onFileSelect={handleFileSelect}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Disclaimer */}
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
        <i className="fas fa-exclamation-triangle mr-1"></i>
        <strong>Attorney Disclaimer:</strong> This Evidence Builder is a self-help organizational
        tool. It does not constitute legal advice. The evaluation of your evidence and its suitability
        for an immigration petition must be assessed by a licensed immigration attorney. USCIS
        officers exercise discretion; no outcome is guaranteed.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload Area sub-component
// ---------------------------------------------------------------------------

function UploadArea({ criterionId, isUploading, fileInputRef, onFileSelect }) {
  const [contextDescription, setContextDescription] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const localInputRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFileSelect(criterionId, file, contextDescription);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onFileSelect(criterionId, file, contextDescription);
  };

  return (
    <div className="space-y-2">
      {/* Context description */}
      <input
        type="text"
        value={contextDescription}
        onChange={(e) => setContextDescription(e.target.value)}
        placeholder="Add a note about this document (optional)"
        className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-400 placeholder-gray-400"
      />

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-4 text-center transition-colors cursor-pointer ${
          isDragOver
            ? 'border-yellow-400 bg-yellow-50'
            : 'border-gray-300 hover:border-yellow-400 hover:bg-yellow-50/30'
        }`}
        onClick={() => localInputRef.current?.click()}
      >
        {isUploading ? (
          <div className="text-gray-500 text-xs">
            <i className="fas fa-spinner fa-spin mr-1 text-yellow-500"></i>
            Uploading...
          </div>
        ) : (
          <div className="text-gray-500 text-xs">
            <i className="fas fa-cloud-upload-alt text-gray-300 text-2xl mb-1 block"></i>
            <span className="font-medium text-gray-700">Drop file here or click to upload</span>
            <br />
            PDF, DOCX, JPG, PNG, TXT · Max 25 MB
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={(el) => {
          localInputRef.current = el;
          if (fileInputRef) fileInputRef(el);
        }}
        type="file"
        accept=".pdf,.docx,.doc,.jpg,.jpeg,.png,.tiff,.txt"
        className="hidden"
        onChange={handleFileChange}
        disabled={isUploading}
      />
    </div>
  );
}
